import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../constants/AuthContext';
import Sidebar from './Sidebar';
import { COLORS } from '../constants/theme';
import { LayoutDashboard, Package, PlusCircle, Receipt, BarChart2, Bell, Moon, Sun, Copy, Building2, Users, Settings, Lock, LogOut, X, Key } from 'lucide-react';

export const ThemeContext = React.createContext({ dark: false, toggle: () => {} });

const tabItems = [
  { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { path: '/products', label: 'Products', icon: Package },
  { path: '/record', label: 'Record', icon: PlusCircle },
  { path: '/history', label: 'History', icon: Receipt },
  { path: '/reports', label: 'Reports', icon: BarChart2 },
];

const menuItems = [
  { icon: Building2, label: 'Business Profile', sub: 'Name, code, details', path: '/profile', ownerOnly: false },
  { icon: Users, label: 'Staff Members', sub: 'View your team', path: '/staff', ownerOnly: true },
  { icon: Settings, label: 'App Settings', sub: 'Theme, preferences', path: '/settings', ownerOnly: false },
  { icon: Lock, label: 'Change Password', sub: 'Update your password', path: '/change-password', ownerOnly: false },
];

function ProfilePanel({ user, onClose, dark }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const headerBg = dark ? '#1a1a1a' : 'white';
  const headerBorder = dark ? '#2a2a2a' : COLORS.border;
  const textColor = dark ? '#ffffff' : COLORS.ink;

  const handleNav = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 300, background: headerBg, zIndex: 301, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ background: COLORS.ink, padding: '32px 24px 24px', position: 'relative' }}>
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={16} color="white" />
          </button>

          <div style={{ width: 56, height: 56, borderRadius: 16, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900, color: 'white', marginBottom: 12 }}>
            {user?.fullName?.charAt(0).toUpperCase()}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>{user?.fullName}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{user?.role} · {user?.businessName}</div>

          {user?.role === 'Owner' && (
            <div
              onClick={() => { navigator.clipboard.writeText(user?.businessCode ?? ''); alert('Business code copied!'); }}
              style={{ marginTop: 14, background: 'rgba(61,170,106,0.15)', border: '1px solid rgba(61,170,106,0.3)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Business Code</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#3DAA6A', letterSpacing: 2 }}>{user?.businessCode}</div>
              </div>
              <Copy size={16} color="#3DAA6A" />
            </div>
          )}
        </div>

        {/* Menu */}
        <div style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.inkLight, letterSpacing: 1.5, padding: '8px 12px', textTransform: 'uppercase', marginBottom: 4 }}>
            Account
          </div>
          {menuItems
            .filter(item => !item.ownerOnly || user?.role === 'Owner')
            .map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '13px 14px', borderRadius: 12, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', marginBottom: 4 }}
                  onMouseEnter={e => e.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: COLORS.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={18} color={COLORS.accent} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: COLORS.inkLight, marginTop: 1 }}>{item.sub}</div>
                  </div>
                </button>
              );
            })}
        </div>

        {/* Logout */}
        <div style={{ padding: '12px 12px 32px' }}>
          <button
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 14px', borderRadius: 12, border: 'none', background: '#FEE2E2', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}
          >
            <LogOut size={18} color={COLORS.danger} />
            <span style={{ color: COLORS.danger }}>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}

function BottomTabBar({ dark }) {
  const navigate = useNavigate();
  const location = useLocation();
  const bg = dark ? '#1a1a1a' : 'white';
  const borderColor = dark ? '#2a2a2a' : COLORS.border;

  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: bg, borderTop: `1px solid ${borderColor}`, display: 'flex', zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)' }}>
      {tabItems.map(item => {
        const active = location.pathname === item.path;
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 4px 8px', border: 'none', background: 'transparent', cursor: 'pointer', color: active ? COLORS.accent : (dark ? '#666' : COLORS.inkLight) }}
          >
            <Icon size={22} strokeWidth={active ? 2.5 : 1.8} />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, marginTop: 3 }}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [dark, setDark] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setCollapsed(window.innerWidth < 900 && !mobile);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const sidebarWidth = collapsed ? 64 : 260;
  const bg = dark ? '#0f0f0f' : COLORS.bg;
  const headerBg = dark ? '#1a1a1a' : 'white';
  const headerBorder = dark ? '#2a2a2a' : COLORS.border;
  const textColor = dark ? '#ffffff' : COLORS.ink;
  const subText = dark ? '#888' : COLORS.inkLight;

  const notifications = [
    { id: 1, text: 'New sale recorded by staff', time: '2 min ago', icon: <PlusCircle size={16} color={COLORS.accent} /> },
    { id: 2, text: 'Stock running low: Indomie', time: '1 hr ago', icon: <Package size={16} color={COLORS.accent} /> },
    { id: 3, text: 'Welcome to StokPilot!', time: 'Today', icon: <BarChart2 size={16} color={COLORS.accent} /> },
  ];

  if (isMobile) {
    return (
      <ThemeContext.Provider value={{ dark, toggle: () => setDark(v => !v) }}>
        <div style={{ background: bg, minHeight: '100vh', paddingBottom: 80 }}>

          {/* Mobile header */}
          <div style={{ position: 'sticky', top: 0, zIndex: 100, background: headerBg, borderBottom: `1px solid ${headerBorder}`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'sans-serif', fontSize: 18, fontWeight: 900 }}>
              <span style={{ color: textColor }}>Stok</span>
              <span style={{ color: COLORS.accent }}>Pilot</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Dark mode */}
              <button
                onClick={() => setDark(v => !v)}
                style={{ background: dark ? '#2a2a2a' : COLORS.bg, border: `1px solid ${headerBorder}`, borderRadius: 8, width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {dark ? <Sun size={16} color="#ffffff" /> : <Moon size={16} color={COLORS.inkMid} />}
              </button>

              {/* Notifications */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowNotifications(v => !v)}
                  style={{ background: dark ? '#2a2a2a' : COLORS.bg, border: `1px solid ${headerBorder}`, borderRadius: 8, width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                >
                  <Bell size={16} color={dark ? '#ffffff' : COLORS.inkMid} />
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: '#ef4444' }} />
                </button>
                {showNotifications && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, width: 280, background: headerBg, borderRadius: 16, border: `1px solid ${headerBorder}`, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden', zIndex: 300 }}>
                    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${headerBorder}`, fontSize: 14, fontWeight: 800, color: textColor }}>Notifications</div>
                    {notifications.map((n, i) => (
                      <div key={n.id} style={{ padding: '12px 16px', borderBottom: i < notifications.length - 1 ? `1px solid ${headerBorder}` : 'none', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: COLORS.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n.icon}</div>
                        <div>
                          <div style={{ fontSize: 13, color: textColor }}>{n.text}</div>
                          <div style={{ fontSize: 11, color: subText, marginTop: 2 }}>{n.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Avatar — opens profile panel */}
              <div
                onClick={() => setShowProfile(true)}
                style={{ width: 36, height: 36, borderRadius: 10, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: 'white', cursor: 'pointer' }}
              >
                {user?.fullName?.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>

          <div style={{ color: textColor }}>{children}</div>
          <BottomTabBar dark={dark} />
        </div>

        {showNotifications && <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowNotifications(false)} />}
        {showProfile && <ProfilePanel user={user} onClose={() => setShowProfile(false)} dark={dark} />}
      </ThemeContext.Provider>
    );
  }

  // Desktop
  return (
    <ThemeContext.Provider value={{ dark, toggle: () => setDark(v => !v) }}>
      <div style={{ display: 'flex', minHeight: '100vh', background: bg }}>
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} />
        <div style={{ marginLeft: sidebarWidth, flex: 1, minHeight: '100vh', transition: 'margin-left 0.25s ease' }}>
          <div style={{ position: 'sticky', top: 0, zIndex: 50, background: headerBg, borderBottom: `1px solid ${headerBorder}`, padding: '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
            <button
              onClick={() => setDark(v => !v)}
              style={{ background: dark ? '#2a2a2a' : COLORS.bg, border: `1px solid ${headerBorder}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {dark ? <Sun size={16} color="#ffffff" /> : <Moon size={16} color={COLORS.inkMid} />}
              <span style={{ fontSize: 12, fontWeight: 600, color: subText }}>{dark ? 'Light' : 'Dark'}</span>
            </button>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(v => !v)}
                style={{ background: dark ? '#2a2a2a' : COLORS.bg, border: `1px solid ${headerBorder}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}
              >
                <Bell size={16} color={dark ? '#ffffff' : COLORS.inkMid} />
                <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: '#ef4444', border: '2px solid white' }} />
              </button>
              {showNotifications && (
                <div style={{ position: 'absolute', top: '110%', right: 0, width: 300, background: headerBg, borderRadius: 16, border: `1px solid ${headerBorder}`, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden', zIndex: 200 }}>
                  <div style={{ padding: '16px 20px', borderBottom: `1px solid ${headerBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: textColor }}>Notifications</div>
                    <span style={{ background: COLORS.accentLight, color: COLORS.accent, fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20 }}>{notifications.length} new</span>
                  </div>
                  {notifications.map((n, i) => (
                    <div key={n.id} style={{ padding: '14px 20px', borderBottom: i < notifications.length - 1 ? `1px solid ${headerBorder}` : 'none', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = dark ? '#222' : COLORS.bg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: COLORS.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n.icon}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: textColor, lineHeight: 1.4 }}>{n.text}</div>
                        <div style={{ fontSize: 11, color: subText, marginTop: 3 }}>{n.time}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ padding: '12px 20px', textAlign: 'center', borderTop: `1px solid ${headerBorder}` }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent, cursor: 'pointer' }}>Mark all as read</span>
                  </div>
                </div>
              )}
            </div>

            {/* Desktop avatar */}
            <div
              onClick={() => setShowProfile(true)}
              style={{ width: 38, height: 38, borderRadius: 10, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: 'white', cursor: 'pointer' }}
            >
              {user?.fullName?.charAt(0).toUpperCase()}
            </div>
          </div>

          <div style={{ color: textColor }}>{children}</div>
        </div>
      </div>

      {showNotifications && <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowNotifications(false)} />}
      {showProfile && <ProfilePanel user={user} onClose={() => setShowProfile(false)} dark={dark} />}
    </ThemeContext.Provider>
  );
}